import React, { useEffect, useState } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/admin/AdminLayout';
import { Users, Wallet, TrendingUp, AlertCircle, IndianRupee, Clock,DollarSign } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const StatCard = ({ icon: Icon, label, value, color = 'primary', sub }) => (
  <div className="bg-dark-700 border border-white/5 rounded-2xl p-5">
    <div className={`w-10 h-10 rounded-xl bg-${color}-500/20 flex items-center justify-center mb-3`}>
      <Icon className={`w-5 h-5 text-${color}-400`} />
    </div>
    <p className="text-2xl font-bold text-white">{value}</p>
    <p className="text-gray-400 text-sm mt-0.5">{label}</p>
    {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
  </div>
);

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get('/api/admin/dashboard')
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <AdminLayout>
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    </AdminLayout>
  );

  const levelChartData = data?.levelStats?.map(l => ({
    name: `L${l._id}`,
    users: l.count
  })).sort((a, b) => a.name.localeCompare(b.name)) || [];

  return (
    <AdminLayout>
      <div className="animate-fade-in">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white">Admin Dashboard</h1>
          <p className="text-gray-400 mt-1">System overview and statistics</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard icon={Users} label="Total Users" value={data?.stats?.totalUsers || 0} sub={`${data?.stats?.activeUsers} active`} />
          <StatCard icon={AlertCircle} label="Inactive Users" value={data?.stats?.inactiveUsers || 0} sub="Wallet depleted" color="red" />
          <StatCard icon={Clock} label="Pending Withdrawals" value={data?.stats?.pendingWithdrawals || 0} sub="Awaiting approval" color="amber" />
          <StatCard icon={DollarSign} label="Total Revenue" value={`$${(data?.stats?.totalRevenue || 0).toFixed(0)}`} sub="Setup fees collected" color="green" />
        </div>

        <div className="grid grid-cols-2 gap-4 mb-6">
          <StatCard icon={TrendingUp} label="Profit Deducted" value={`$${(data?.stats?.totalProfitDeducted || 0).toFixed(0)}`} sub="Total from users" color="blue" />
          <StatCard icon={Wallet} label="Withdrawals Paid" value={`$${(data?.stats?.totalWithdrawalsPaid || 0).toFixed(0)}`} sub="Total paid out" color="purple" />
        </div>

        {/* Charts + Recent users */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Level distribution chart */}
          <div className="bg-dark-700 border border-white/5 rounded-2xl p-5">
            <h3 className="text-white font-semibold mb-4">Users by Level</h3>
            {levelChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={levelChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
                  <XAxis dataKey="name" stroke="#6b7280" tick={{ fontSize: 12 }} />
                  <YAxis stroke="#6b7280" tick={{ fontSize: 12 }} />
                  <Tooltip contentStyle={{ background: '#1a2540', border: '1px solid #ffffff20', borderRadius: '8px', color: '#fff' }} />
                  <Bar dataKey="users" fill="#22c55e" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-48 text-gray-500">No level data yet</div>
            )}
          </div>

          {/* Recent Users */}
          <div className="bg-dark-700 border border-white/5 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-semibold">Recent Users</h3>
              <a href="/admin/users" className="text-primary-400 hover:text-primary-300 text-sm">View all</a>
            </div>
            <div className="space-y-3">
              {data?.recentUsers?.map(u => (
                <div key={u._id} className="flex items-center gap-3 py-2 border-b border-white/5 last:border-0">
                  <div className="w-8 h-8 rounded-full bg-primary-500/20 flex items-center justify-center text-primary-400 font-semibold text-sm flex-shrink-0">
                    {u.name[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium truncate">{u.name}</p>
                    <p className="text-gray-500 text-xs truncate">{u.email}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className={`w-2 h-2 rounded-full ${u.isActive ? 'bg-primary-500' : 'bg-red-500'}`}></span>
                    <span className="text-xs text-gray-400">L{u.level}</span>
                  </div>
                </div>
              ))}
              {!data?.recentUsers?.length && <p className="text-gray-500 text-center py-4">No users yet</p>}
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
